import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Collapse, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, FormControlLabel, Checkbox, CircularProgress, Alert, Tooltip, Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Block as RetireIcon,
  LocalShipping as ReceiveIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';
import { deaLogsApi } from '../../utils/api';
import { fmtQty } from './format';
import { isCalendarDayBeforeToday } from '../../utils/dateUtils';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import { useRBAC } from '../../auth/useRBAC';
import { inventoryControls } from '../../auth/pageControls';
import type {
  ControlledSubstance, ControlledSubstanceLot, DeaSchedule, ControlledSubstanceDosageForm,
} from '../../types/treatmentSheet';

const SCHEDULE_LABEL: Record<DeaSchedule, string> = {
  SCHEDULE_II: 'C-II', SCHEDULE_III: 'C-III', SCHEDULE_IV: 'C-IV', SCHEDULE_V: 'C-V',
};
const SCHEDULE_COLOR: Record<DeaSchedule, 'error' | 'warning' | 'info' | 'default'> = {
  SCHEDULE_II: 'error', SCHEDULE_III: 'warning', SCHEDULE_IV: 'info', SCHEDULE_V: 'default',
};
const SCHEDULES: DeaSchedule[] = ['SCHEDULE_II', 'SCHEDULE_III', 'SCHEDULE_IV', 'SCHEDULE_V'];
const DOSAGE_FORMS: ControlledSubstanceDosageForm[] =
  ['INJECTABLE', 'TABLET', 'CAPSULE', 'LIQUID', 'PATCH', 'POWDER', 'OTHER'];

const num = (v: number | string | null | undefined) => (v == null ? 0 : Number(v));

// ─── Add Substance dialog ───────────────────────────────────────────────────

interface AddSubstanceDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** Prefill (the Inventory "Not DEA-tracked" chip) — strength/form/unit stay manual. */
  initialGenericName?: string | null;
}

const AddSubstanceDialog: React.FC<AddSubstanceDialogProps> = ({ open, onClose, onCreated, initialGenericName }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [genericName, setGenericName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [deaSchedule, setDeaSchedule] = useState<DeaSchedule | ''>('');
  const [ndcCode, setNdcCode] = useState('');
  const [strength, setStrength] = useState('');
  const [dosageForm, setDosageForm] = useState<ControlledSubstanceDosageForm | ''>('');
  const [unit, setUnit] = useState('');
  const [bottleCount, setBottleCount] = useState('');
  const [bottleSize, setBottleSize] = useState('');
  const [requiresWitness, setRequiresWitness] = useState(true);

  useEffect(() => {
    if (open && initialGenericName) setGenericName(initialGenericName);
  }, [open, initialGenericName]);

  const reset = () => {
    setGenericName(''); setBrandName(''); setDeaSchedule(''); setNdcCode('');
    setStrength(''); setDosageForm(''); setUnit('');
    setBottleCount(''); setBottleSize(''); setRequiresWitness(true);
  };

  const mutation = useMutation(
    () => deaLogsApi.createSubstance({
      genericName: genericName.trim(),
      brandName: brandName.trim() || undefined,
      deaSchedule: deaSchedule as DeaSchedule,
      ndcCode: ndcCode.trim() || undefined,
      strength: strength.trim(),
      dosageForm: dosageForm as ControlledSubstanceDosageForm,
      unit: unit.trim(),
      bottleCount: bottleCount.trim() ? Number(bottleCount) : undefined,
      bottleSize: bottleSize.trim() || undefined,
      requiresWitness,
    }),
    {
      onSuccess: () => {
        enqueueSnackbar('Controlled substance added', { variant: 'success' });
        reset();
        onCreated();
        onClose();
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message ?? 'Failed to add substance', { variant: 'error' });
      },
    },
  );

  const canSubmit =
    genericName.trim() && deaSchedule && strength.trim() && dosageForm && unit.trim();

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle>Add Controlled Substance</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            label="Generic Name" required size="small" fullWidth
            value={genericName} onChange={(e) => setGenericName(e.target.value)}
            placeholder="e.g. Butorphanol"
          />
          <TextField
            label="Brand Name" size="small" fullWidth
            value={brandName} onChange={(e) => setBrandName(e.target.value)}
            placeholder="e.g. Torbugesic"
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select label="DEA Schedule" required size="small" fullWidth
              value={deaSchedule} onChange={(e) => setDeaSchedule(e.target.value as DeaSchedule)}
            >
              {SCHEDULES.map((s) => (
                <MenuItem key={s} value={s}>{SCHEDULE_LABEL[s]}</MenuItem>
              ))}
            </TextField>
            <TextField
              select label="Dosage Form" required size="small" fullWidth
              value={dosageForm} onChange={(e) => setDosageForm(e.target.value as ControlledSubstanceDosageForm)}
            >
              {DOSAGE_FORMS.map((f) => (
                <MenuItem key={f} value={f}>{f.charAt(0) + f.slice(1).toLowerCase()}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Strength" required size="small" fullWidth
              value={strength} onChange={(e) => setStrength(e.target.value)}
              placeholder="e.g. 10 mg/mL"
            />
            <TextField
              label="Unit" required size="small" fullWidth
              value={unit} onChange={(e) => setUnit(e.target.value)}
              placeholder="e.g. mL, tablet, patch"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Number of Bottles" type="number" size="small" fullWidth
              value={bottleCount} onChange={(e) => setBottleCount(e.target.value)}
              inputProps={{ min: 0 }}
              placeholder="e.g. 5"
            />
            <TextField
              label="Bottle Size" size="small" fullWidth
              value={bottleSize} onChange={(e) => setBottleSize(e.target.value)}
              placeholder="e.g. 100 mL"
            />
          </Box>
          <TextField
            label="NDC Code" size="small" fullWidth
            value={ndcCode} onChange={(e) => setNdcCode(e.target.value)}
            placeholder="optional"
          />
          <FormControlLabel
            control={<Checkbox checked={requiresWitness} onChange={(e) => setRequiresWitness(e.target.checked)} />}
            label="Requires a witness on administration"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit || mutation.isLoading}
          onClick={() => mutation.mutate()}
          startIcon={mutation.isLoading ? <CircularProgress size={14} /> : <AddIcon />}
        >
          Add Substance
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Receive Lot dialog ──────────────────────────────────────────────────────

interface ReceiveLotDialogProps {
  substance: ControlledSubstance | null;
  onClose: () => void;
  onReceived: () => void;
}

const ReceiveLotDialog: React.FC<ReceiveLotDialogProps> = ({ substance, onClose, onReceived }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [lotNumber, setLotNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [quantityReceived, setQuantityReceived] = useState('');
  const [receivedFromSupplier, setReceivedFromSupplier] = useState('');
  const [form222Number, setForm222Number] = useState('');

  const reset = () => {
    setLotNumber(''); setExpirationDate(''); setQuantityReceived('');
    setReceivedFromSupplier(''); setForm222Number('');
  };

  const mutation = useMutation(
    () => deaLogsApi.createLot({
      substanceId: substance!.id,
      lotNumber: lotNumber.trim(),
      expirationDate: new Date(expirationDate).toISOString(),
      quantityReceived: Number(quantityReceived),
      receivedFromSupplier: receivedFromSupplier.trim() || undefined,
      form222Number: form222Number.trim() || undefined,
    }),
    {
      onSuccess: () => {
        enqueueSnackbar('Lot received', { variant: 'success' });
        reset();
        onReceived();
        onClose();
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message ?? 'Failed to receive lot', { variant: 'error' });
      },
    },
  );

  const isScheduleII = substance?.deaSchedule === 'SCHEDULE_II';
  const canSubmit =
    lotNumber.trim() && expirationDate && quantityReceived &&
    !isNaN(Number(quantityReceived)) && Number(quantityReceived) > 0;

  return (
    <Dialog open={!!substance} onClose={() => { reset(); onClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle>
        Receive Lot
        {substance && (
          <Typography variant="caption" color="text.secondary" display="block">
            {substance.genericName} · {substance.strength}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Lot Number" required size="small" fullWidth
              value={lotNumber} onChange={(e) => setLotNumber(e.target.value)}
            />
            <TextField
              label="Expiration" type="date" required size="small" fullWidth
              value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={`Quantity Received${substance ? ` (${substance.unit})` : ''}`}
              type="number" required size="small" fullWidth
              value={quantityReceived} onChange={(e) => setQuantityReceived(e.target.value)}
              inputProps={{ step: 0.001, min: 0 }}
            />
            <TextField
              label="Supplier" size="small" fullWidth
              value={receivedFromSupplier} onChange={(e) => setReceivedFromSupplier(e.target.value)}
              placeholder="optional"
            />
          </Box>
          {isScheduleII && (
            <TextField
              label="DEA Form 222 Number" size="small" fullWidth
              value={form222Number} onChange={(e) => setForm222Number(e.target.value)}
              helperText="Schedule II purchases are ordered on a DEA Form 222."
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit || mutation.isLoading}
          onClick={() => mutation.mutate()}
          startIcon={mutation.isLoading ? <CircularProgress size={14} /> : <ReceiveIcon />}
        >
          Receive Lot
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main inventory view ──────────────────────────────────────────────────────

interface ControlledSubstanceInventoryProps {
  enrolName?: string | null;
  onEnrolConsumed?: () => void;
}

const ControlledSubstanceInventory: React.FC<ControlledSubstanceInventoryProps> = ({ enrolName, onEnrolConsumed }) => {
  const queryClient = useQueryClient();
  // dea-logs.controller.ts createSubstance :36-37, deactivateSubstance :42-43 and createLot
  // :73-74 all declare manage:medical_records (INV-108) - the registry is a clinical record, and
  // `manage:controlled_substances` is a slug no route requires.
  const rbac = useRBAC();
  const can = inventoryControls(rbac);
  // Hoisted: the per-lot expiry check below runs inside a .map().
  const clinicZone = useClinicTimezone();
  const { enqueueSnackbar } = useSnackbar();
  const [showRetired, setShowRetired] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // Enrol request from the Inventory chip: open the Add dialog prefilled.
  useEffect(() => {
    if (enrolName) setAddOpen(true);
  }, [enrolName]);
  const [receiveFor, setReceiveFor] = useState<ControlledSubstance | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [retireConfirm, setRetireConfirm] = useState<ControlledSubstance | null>(null);

  const substancesQuery = useQuery(
    ['controlled-substances', showRetired],
    () => deaLogsApi.listSubstances(showRetired),
  );

  // All non-disposed lots for the clinic, grouped by substance for counts + on-hand.
  const lotsQuery = useQuery(
    ['dea-lots-all'],
    () => deaLogsApi.listLots(undefined, false),
  );

  const lotsBySubstance = (lotsQuery.data ?? []).reduce<Record<string, ControlledSubstanceLot[]>>(
    (acc, lot) => {
      (acc[lot.substanceId] ??= []).push(lot);
      return acc;
    },
    {},
  );

  const invalidate = () => {
    queryClient.invalidateQueries(['controlled-substances']);
    queryClient.invalidateQueries(['dea-lots-all']);
    queryClient.invalidateQueries(['dea-reconciliation']);
  };

  const deactivateMutation = useMutation(
    (id: string) => deaLogsApi.deactivateSubstance(id),
    {
      onSuccess: () => {
        enqueueSnackbar('Substance retired', { variant: 'success' });
        setRetireConfirm(null);
        invalidate();
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message ?? 'Failed to retire substance', { variant: 'error' });
      },
    },
  );

  const substances = substancesQuery.data ?? [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Manage your controlled-substance catalog and receive lots. Substances and lots are required
          before a controlled-substance order can be administered.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />}
            label={<Typography variant="caption">Show retired</Typography>}
          />
          {can.deaWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
              Add Substance
            </Button>
          )}
        </Box>
      </Box>

      {substancesQuery.isError && <Alert severity="error">Failed to load controlled substances.</Alert>}
      {substancesQuery.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      )}

      {!substancesQuery.isLoading && substances.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" gutterBottom>
            No controlled substances yet.
          </Typography>
          {can.deaWrite && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
              Add your first substance
            </Button>
          )}
        </Paper>
      )}

      {substances.length > 0 && (
        <Paper sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Substance</TableCell>
                <TableCell>Sched</TableCell>
                <TableCell>Strength</TableCell>
                <TableCell>Form</TableCell>
                <TableCell align="right">Active Lots</TableCell>
                <TableCell align="right">On Hand</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {substances.map((sub) => {
                const lots = lotsBySubstance[sub.id] ?? [];
                const onHand = lots.reduce((sum, l) => sum + num(l.quantityRemaining), 0);
                const expanded = expandedId === sub.id;
                return (
                  <React.Fragment key={sub.id}>
                    <TableRow hover sx={{ opacity: sub.isActive ? 1 : 0.55 }}>
                      <TableCell padding="checkbox">
                        <IconButton size="small" onClick={() => setExpandedId(expanded ? null : sub.id)}>
                          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <strong>{sub.genericName}</strong>
                        {sub.brandName && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {sub.brandName}
                          </Typography>
                        )}
                        {(sub.bottleCount != null || sub.bottleSize) && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {[
                              sub.bottleCount != null ? `${sub.bottleCount} bottle${sub.bottleCount === 1 ? '' : 's'}` : null,
                              sub.bottleSize ? sub.bottleSize : null,
                            ].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                        {!sub.isActive && (
                          <Chip label="Retired" size="small" sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={SCHEDULE_LABEL[sub.deaSchedule]} color={SCHEDULE_COLOR[sub.deaSchedule]} size="small" />
                      </TableCell>
                      <TableCell>{sub.strength}</TableCell>
                      <TableCell>{sub.dosageForm.charAt(0) + sub.dosageForm.slice(1).toLowerCase()}</TableCell>
                      <TableCell align="right">{lots.length}</TableCell>
                      <TableCell align="right">{onHand.toFixed(3)} {sub.unit}</TableCell>
                      <TableCell align="right">
                        {can.deaWrite && (
                          <Tooltip title="Receive a new lot">
                            <span>
                              <IconButton size="small" disabled={!sub.isActive} onClick={() => setReceiveFor(sub)}>
                                <ReceiveIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {can.deaWrite && sub.isActive && (
                          <Tooltip title="Retire substance">
                            <IconButton size="small" color="error" onClick={() => setRetireConfirm(sub)}>
                              <RetireIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                        <Collapse in={expanded} unmountOnExit>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Lots</Typography>
                            {lots.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No active lots. Use the receive (truck) action to add one.
                              </Typography>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Lot #</TableCell>
                                    <TableCell>Expires</TableCell>
                                    <TableCell align="right">Received</TableCell>
                                    <TableCell align="right">Remaining</TableCell>
                                    <TableCell>Supplier</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {lots.map((lot) => {
                                    // CALENDAR day, not a moment - a lot is
                                    // in date THROUGH its expiry day in the
                                    // clinic's zone. See the note in
                                    // ControlledSubstanceAdminDialog.tsx.
                                    const expired = isCalendarDayBeforeToday(lot.expirationDate, clinicZone);
                                    return (
                                      <TableRow key={lot.id}>
                                        <TableCell>{lot.lotNumber}</TableCell>
                                        <TableCell>
                                          {format(parseISO(lot.expirationDate), 'MMM yyyy')}
                                          {expired && <Chip label="expired" size="small" color="warning" sx={{ ml: 1 }} />}
                                        </TableCell>
                                        <TableCell align="right">{fmtQty(lot.quantityReceived, sub.unit, 3)}</TableCell>
                                        <TableCell align="right">{fmtQty(lot.quantityRemaining, sub.unit, 3)}</TableCell>
                                        <TableCell>{lot.receivedFromSupplier ?? '—'}</TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <AddSubstanceDialog
        open={addOpen}
        onClose={() => { setAddOpen(false); onEnrolConsumed?.(); }}
        onCreated={invalidate}
        initialGenericName={enrolName}
      />
      <ReceiveLotDialog substance={receiveFor} onClose={() => setReceiveFor(null)} onReceived={invalidate} />

      <Dialog open={!!retireConfirm} onClose={() => setRetireConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Retire Substance?</DialogTitle>
        <DialogContent>
          <Typography>
            Retire <strong>{retireConfirm?.genericName}</strong>? It will no longer be selectable for new
            orders. Existing lots and historical records are preserved for DEA recordkeeping.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetireConfirm(null)}>Cancel</Button>
          <Button
            color="error" variant="contained"
            disabled={deactivateMutation.isLoading}
            onClick={() => retireConfirm && deactivateMutation.mutate(retireConfirm.id)}
          >
            Retire
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ControlledSubstanceInventory;
